import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { FaCog, FaBell, FaBellSlash, FaVolumeUp, FaVolumeMute, FaArrowLeft } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { localDB } from '../../../../../../../../../../crud/localDB/localDB';
import './NotificationConfig.scss';

export const NOTIFICATION_TOPICS = [
  'general',
  'stock',
  'pedidos',
  'usuarios',
  'reservas',
  'caja',
  'configuracion',
  'reportes',
  'impresion',
  'clientes',
  'facturas',
] as const;

export type NotificationTopic = typeof NOTIFICATION_TOPICS[number];

export interface TopicSettings {
  enabled: boolean;
  soundEnabled: boolean;
  [k: string]: boolean; // Index signature for JsonValue compatibility
}

export interface NotificationSettings {
  enabled: boolean;
  soundVolume: number;
  topicSettings: Record<NotificationTopic, TopicSettings>;
}

interface NotificationConfigProps {
  onBack: () => void;
}

const TOPIC_LABELS: Record<NotificationTopic, string> = {
  general: 'General',
  stock: 'Inventario',
  pedidos: 'Pedidos',
  usuarios: 'Usuarios',
  reservas: 'Reservas',
  caja: 'Caja',
  configuracion: 'Configuración',
  reportes: 'Reportes',
  impresion: 'Impresión',
  clientes: 'Clientes',
  facturas: 'Facturas',
};

const getDefaultSettings = (): NotificationSettings => {
  const topicSettings: Record<string, TopicSettings> = {};
  NOTIFICATION_TOPICS.forEach(topic => {
    topicSettings[topic] = {
      enabled: true,
      soundEnabled: false, // Sound disabled by default
    };
  });

  return {
    enabled: true,
    soundVolume: 50,
    topicSettings: topicSettings as Record<NotificationTopic, TopicSettings>,
  };
};

const NotificationConfig: React.FC<NotificationConfigProps> = ({ onBack }) => {
  const [settings, setSettings] = useState<NotificationSettings>(getDefaultSettings());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const [enabled, volume, topicSettings] = await Promise.all([
        localDB.get<boolean>('notifications', 'enabled'),
        localDB.get<number>('notifications', 'soundVolume'),
        localDB.get<Record<NotificationTopic, TopicSettings>>('notifications', 'topicSettings'),
      ]);

      const defaults = getDefaultSettings();

      setSettings({
        enabled: enabled ?? defaults.enabled,
        soundVolume: volume ?? defaults.soundVolume,
        topicSettings: topicSettings ?? defaults.topicSettings,
      });
    } catch (error) {
      console.error('Error loading notification settings:', error);
      toast.error('Error al cargar configuración');
      setSettings(getDefaultSettings());
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (newSettings: NotificationSettings) => {
    try {
      await Promise.all([
        localDB.set('notifications', 'enabled', newSettings.enabled),
        localDB.set('notifications', 'soundVolume', newSettings.soundVolume),
        localDB.set('notifications', 'topicSettings', newSettings.topicSettings),
      ]);
      setSettings(newSettings);
    } catch (error) {
      console.error('Error saving notification settings:', error);
      toast.error('Error al guardar configuración');
    }
  };

  const handleMasterToggle = async () => {
    const newSettings = { ...settings, enabled: !settings.enabled };
    await saveSettings(newSettings);
    toast.success(newSettings.enabled ? 'Notificaciones activadas' : 'Notificaciones desactivadas');
  };

  const handleTopicToggle = async (topic: NotificationTopic) => {
    const newSettings = {
      ...settings,
      topicSettings: {
        ...settings.topicSettings,
        [topic]: {
          ...settings.topicSettings[topic],
          enabled: !settings.topicSettings[topic].enabled,
        },
      },
    };
    await saveSettings(newSettings);
  };

  const handleSoundToggle = async (topic: NotificationTopic) => {
    const newSettings = {
      ...settings,
      topicSettings: {
        ...settings.topicSettings,
        [topic]: {
          ...settings.topicSettings[topic],
          soundEnabled: !settings.topicSettings[topic].soundEnabled,
        },
      },
    };
    await saveSettings(newSettings);
  };

  const handleVolumeChange = async (volume: number) => {
    const newSettings = { ...settings, soundVolume: volume };
    await saveSettings(newSettings);
  };

  const handleReset = async () => {
    const defaults = getDefaultSettings();
    await saveSettings(defaults);
    toast.success('Configuración restablecida');
  };

  const testSound = () => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      const volumeLevel = settings.soundVolume / 100;
      gainNode.gain.setValueAtTime(volumeLevel, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.2);
      
      toast.success('Sonido de prueba reproducido');
    } catch (err) {
      console.error('Error playing test sound:', err);
      toast.error('No se pudo reproducir el sonido de prueba');
    }
  };

  if (loading) {
    return (
      <div className="notification-config-loading">
        <p>Cargando configuración...</p>
      </div>
    );
  }

  return (
    <div className="notification-config-content">
      {/* Header with Back Button */}
      <div className="config-header">
        <button className="back-btn" onClick={onBack}>
          <FaArrowLeft />
          <span>Volver</span>
        </button>
        <h2>
          <FaCog style={{ marginRight: '8px' }} />
          Configuración de Notificaciones
        </h2>
      </div>

      <div className="config-body">
        {/* Master Switch */}
        <div className="config-section master-section">
            <div className="section-header">
              <div className="header-info">
                <h3>Control General</h3>
                <p>Activa o desactiva todas las notificaciones</p>
              </div>
              <label className="config-switch">
                <input
                  type="checkbox"
                  checked={settings.enabled}
                  onChange={handleMasterToggle}
                />
                <span className="slider">
                  <span className="knob">
                    {settings.enabled ? <FaBell size={12} /> : <FaBellSlash size={12} />}
                  </span>
                </span>
              </label>
            </div>
          </div>

          {/* Volume Control */}
          <div className="config-section volume-section">
            <div className="section-header">
              <div className="header-info">
                <h3>Volumen de Sonido</h3>
                <p>Ajusta el volumen global de las notificaciones</p>
              </div>
              <div className="volume-display">
                {settings.soundVolume === 0 ? <FaVolumeMute /> : <FaVolumeUp />}
                <span>{settings.soundVolume}%</span>
              </div>
            </div>
            <div className="volume-controls">
              <input
                type="range"
                min="0"
                max="100"
                value={settings.soundVolume}
                onChange={(e) => handleVolumeChange(Number(e.target.value))}
                className="volume-slider"
                disabled={!settings.enabled}
              />
              <Button
                variant="outline-primary"
                size="sm"
                onClick={testSound}
                disabled={!settings.enabled || settings.soundVolume === 0}
                className="test-sound-btn"
              >
                Probar Sonido
              </Button>
            </div>
          </div>

          {/* Topic Settings */}
          <div className="config-section topics-section">
            <div className="section-header">
              <div className="header-info">
                <h3>Configuración por Tema</h3>
                <p>Controla notificaciones y sonidos individuales</p>
              </div>
            </div>
            <div className="topics-grid">
              {NOTIFICATION_TOPICS.map(topic => (
                <div key={topic} className="topic-item">
                  <div className="topic-header">
                    <span className="topic-name">{TOPIC_LABELS[topic]}</span>
                    <div className="topic-controls">
                      <button
                        className={`icon-btn ${settings.topicSettings[topic].soundEnabled ? 'active' : ''}`}
                        onClick={() => handleSoundToggle(topic)}
                        disabled={!settings.enabled || !settings.topicSettings[topic].enabled}
                        title={settings.topicSettings[topic].soundEnabled ? 'Desactivar sonido' : 'Activar sonido'}
                      >
                        {settings.topicSettings[topic].soundEnabled ? (
                          <FaVolumeUp size={14} />
                        ) : (
                          <FaVolumeMute size={14} />
                        )}
                      </button>
                      <label className="config-switch small">
                        <input
                          type="checkbox"
                          checked={settings.topicSettings[topic].enabled}
                          onChange={() => handleTopicToggle(topic)}
                          disabled={!settings.enabled}
                        />
                        <span className="slider">
                          <span className="knob"></span>
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="config-footer">
            <Button variant="secondary" onClick={handleReset}>
              Restablecer
            </Button>
          </div>
        </div>
      </div>
  );
};

export default NotificationConfig;
